import { useState } from "react";

interface VerificationEntry {
  id: string;
  agentName: string;
  outputType: string;
  verdict: "PASS" | "FAIL" | "CONDITIONAL" | "BLOCK" | null;
  status: string;
  iteration: number;
  maxIterations: number;
  timestamp: string;
  /** Optional — only present on entries from /api/verifications/recent */
  feedback?: string[];
}

const VERDICT_TOKENS = {
  PASS:        { color: "var(--color-status-active)", border: "rgba(132, 204, 22, 0.4)", bg: "rgba(132, 204, 22, 0.08)" },
  FAIL:        { color: "var(--color-status-error)",  border: "rgba(239, 68, 68, 0.4)",  bg: "rgba(239, 68, 68, 0.08)"  },
  CONDITIONAL: { color: "var(--color-status-paused)", border: "rgba(245, 158, 11, 0.45)", bg: "rgba(245, 158, 11, 0.10)" },
  BLOCK:       { color: "#fca5a5",                    border: "rgba(239, 68, 68, 0.6)",  bg: "rgba(239, 68, 68, 0.15)"  },
} as const;

interface VerificationLogProps {
  entries: VerificationEntry[];
  /** Visible subtitle next to the entry count. Default "live". */
  subtitle?: string;
}

function formatTimestamp(ts: string) {
  const d = new Date(ts);
  return d.toLocaleTimeString("en-GB", { hour12: false });
}

export function VerificationLog({ entries, subtitle = "live" }: VerificationLogProps) {
  if (entries.length === 0) {
    return (
      <div className="panel p-6">
        <h3 className="eyebrow">Verification Log</h3>
        <p className="mt-3 text-sm text-[var(--color-text-dim)]">No verifications yet</p>
      </div>
    );
  }

  return (
    <div className="panel p-6 relative">
      <span className="absolute top-0 left-0 w-12 h-px bg-[var(--color-accent)]" />
      <span className="absolute top-0 left-0 w-px h-12 bg-[var(--color-accent)]" />

      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="eyebrow">Verification Log</p>
          <p className="font-display text-2xl italic mt-1 text-[var(--color-text)]">
            Recent Verdicts
          </p>
        </div>
        <p className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)]">
          {entries.length} entries · {subtitle}
        </p>
      </div>

      <div className="space-y-1.5">
        {entries.map((entry, i) => (
          <LogRow key={entry.id} entry={entry} index={i} />
        ))}
      </div>
    </div>
  );
}

function LogRow({ entry, index }: { entry: VerificationEntry; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const v = entry.verdict ? VERDICT_TOKENS[entry.verdict] : null;
  const expandable =
    !!entry.feedback &&
    entry.feedback.length > 0 &&
    (entry.verdict === "FAIL" || entry.verdict === "CONDITIONAL" || entry.verdict === "BLOCK");

  return (
    <div
      className="reveal rounded-lg border border-[var(--color-border)]/60 hover:border-[var(--color-border-strong)] bg-[var(--color-bg-elev)]/40 transition-colors"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div
        className={`grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3 px-3 py-2.5 group ${
          expandable ? "cursor-pointer" : ""
        }`}
        onClick={expandable ? () => setExpanded((x) => !x) : undefined}
      >
        <span className="font-mono text-[10px] text-[var(--color-text-dim)] tabular-nums">
          {String(index + 1).padStart(2, "0")}
        </span>

        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-display italic text-base text-[var(--color-text)] truncate">
            {entry.agentName}
          </span>
          <span className="font-mono text-[10px] tracking-wider uppercase text-[var(--color-text-dim)] shrink-0">
            · {entry.outputType}
          </span>
          {expandable && (
            <span className="font-mono text-[9px] text-[var(--color-text-dim)] shrink-0">
              {expanded ? "▾" : "▸"}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <span className="font-mono text-[10px] tabular-nums text-[var(--color-text-muted)]">
            iter <span className="text-[var(--color-text)]">{entry.iteration}</span>
            <span className="text-[var(--color-text-dim)]">/{entry.maxIterations}</span>
          </span>

          {entry.verdict && v && (
            <span
              className="pill"
              style={{ color: v.color, borderColor: v.border, background: v.bg }}
            >
              {entry.verdict}
            </span>
          )}

          <span className="font-mono text-[10px] tabular-nums text-[var(--color-text-dim)] w-16 text-right">
            {formatTimestamp(entry.timestamp)}
          </span>
        </div>
      </div>

      {expandable && expanded && entry.feedback && (
        <div className="border-t border-[var(--color-border)]/40 px-3 py-2.5 pl-9 space-y-1">
          {entry.feedback.map((line, j) => (
            <p
              key={j}
              className="font-mono text-[11px] leading-relaxed text-[var(--color-text-muted)]"
            >
              <span className="text-[var(--color-text-dim)]">→</span> {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
